import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  FormControlLabel,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  AutocompleteOptions,
  Button,
  FullScreenLoader,
  SelectFieldWrapper,
} from '@Components/index';
import Modal from '@Components/Modal';
import Radio from '@Components/Radio/Radio.component';
import { StyledRadioGroup } from '@Components/ReportConfig/ReportConfig.styles';
import Icons from '@Constants/Icons.constants';
import { VoiceProvider, VonageMode } from '@Models/callerIds.models';
import {
  useChangeVoiceProviderSettingMutation,
  useGetVoiceProviderOptionsSettingQuery,
} from '@Services/settings.service';
import { BaseAlertProps } from '@Typings/global.types';
import { VoiceProviderSelectOptions } from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

import {
  VoiceProviderModalInput,
  VoiceProviderModalProps,
} from './VoiceProviderModal.types';

const VoiceProviderModal: FunctionComponent<VoiceProviderModalProps> = (
  props: VoiceProviderModalProps
): JSX.Element => {
  const { isOpen, setIsOpen, userId, userName } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [isSuccessfullyModified, setIsSuccessfullyModified] =
    useState<boolean>(false);
  const [isVonageProvider, setIsVonageProvider] = useState<boolean>(false);
  const [vonageMode, setVonageMode] = useState<VonageMode>(VonageMode.OLD);

  // load voice provider settings
  const {
    data: dataOfVoiceProviderOptions,
    isLoading: loadingVoiceProviderOptions,
    isFetching: fetchingVoiceProviderOptions,
    isError: isErrorForGetVoiceProviderOptions,
    error: errorForGetVoiceProviderOptions,
  } = useGetVoiceProviderOptionsSettingQuery(userId);

  // change voice provider settings value by user id
  const [changeVoiceProviderSetting, changeVoiceProviderSettingMethods] =
    useChangeVoiceProviderSettingMutation();

  const isLoading = loadingVoiceProviderOptions || fetchingVoiceProviderOptions;

  const voiceProviderError = t(
    'common.formErrorMessages.voice_provider_required'
  );

  const oldVonageTooltip = t('userCallerIds.Vonage Mode Old Description');
  const newVonageTooltip = t('userCallerIds.Vonage Mode New Description');

  const schema = yup
    .object({
      provider: yup
        .object()
        .nullable()
        .required(voiceProviderError)
        .shape({
          label: yup.string().required(voiceProviderError),
          value: yup.string().required(voiceProviderError),
        }),
    })
    .required();

  const options = VoiceProviderSelectOptions();

  /** FORM METHODS */
  const methods = useForm<VoiceProviderModalInput>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    shouldUnregister: true,
  });

  useEffect(() => {
    if (dataOfVoiceProviderOptions) {
      const providerName = dataOfVoiceProviderOptions.voiceProvider?.toString();
      const providerLabel =
        providerName.toLowerCase().charAt(0).toUpperCase() +
        providerName.toLowerCase().slice(1);
      methods.reset({
        provider: {
          label: providerLabel,
          value: dataOfVoiceProviderOptions.voiceProvider,
        },
      });

      if (providerName === VoiceProvider.VONAGE) {
        setIsVonageProvider(true);
        setVonageMode(dataOfVoiceProviderOptions.vonageMode || VonageMode.OLD);
      } else {
        setIsVonageProvider(false);
        setVonageMode(VonageMode.OLD);
      }
    }
  }, [methods, dataOfVoiceProviderOptions]);

  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<VoiceProviderModalInput> = (formData) => {
    if (formData && formData.provider) {
      return changeVoiceProviderSetting({
        userId,
        voiceProvider: formData.provider.value as VoiceProvider,
        vonageMode,
      });
    }
  };

  /**
   * Error and Success alerts for changing voice provider settings
   */
  useEffect(() => {
    if (changeVoiceProviderSettingMethods.error) {
      triggerAlert(changeVoiceProviderSettingMethods.error, setAlertState);
    }
    if (changeVoiceProviderSettingMethods.isSuccess) {
      setIsSuccessfullyModified(true);
    }
  }, [
    changeVoiceProviderSettingMethods.error,
    triggerAlert,
    changeVoiceProviderSettingMethods,
  ]);

  /**
   * Error Alerts for the Get voice provider settings
   */
  useEffect(() => {
    if (isErrorForGetVoiceProviderOptions && errorForGetVoiceProviderOptions) {
      triggerAlert(errorForGetVoiceProviderOptions, setAlertState);
    }
  }, [
    triggerAlert,
    errorForGetVoiceProviderOptions,
    isErrorForGetVoiceProviderOptions,
  ]);

  const successfullyModifiedInfoSection = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      my={4}
    >
      <SvgIcon
        sx={{
          width: 33.3,
          height: 33.3,
          mb: 1.4,
          color: '#178549',
        }}
      >
        {Icons.checkCircle}
      </SvgIcon>
      <Typography fontSize="18px" fontWeight="600">
        <Trans
          values={{ userName }}
          i18nKey="userCallerIds.Changed Provider"
          components={{ strong: <strong /> }}
        />
      </Typography>
    </Box>
  );

  /** Handles Vonage Mode Radio Button Group Change */
  const handleVonageModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setVonageMode((event.target as HTMLInputElement).value as VonageMode);
  };

  /** Handles Provider Value Change */
  const handleVoiceProviderChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: AutocompleteOptions
  ) => {
    const currentProvider = value.value;

    if (currentProvider === VoiceProvider.VONAGE) {
      setIsVonageProvider(true);
    } else {
      setIsVonageProvider(false);
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        headerText={t('userCallerIds.Voice Provider Settings')}
        onClose={() => {
          setIsOpen(false);
          setIsSuccessfullyModified(false);
        }}
        alertState={alertState}
      >
        <FormProvider {...methods}>
          {!isSuccessfullyModified && (
            <>
              {!isLoading ? (
                <Box
                  component="form"
                  role="form"
                  onSubmit={methods.handleSubmit(onSubmit)}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ padding: '3rem 2.4rem 2.4rem' }}
                  >
                    <Typography variant="main" mb={4} fontSize="16px">
                      <Trans
                        values={{ userName }}
                        i18nKey="userCallerIds.Voice Provider Message"
                        components={{ strong: <strong /> }}
                      />
                    </Typography>
                    <SelectFieldWrapper
                      name="provider"
                      options={options}
                      multiple={false}
                      disableClearable
                      textFieldProps={{
                        label: `${t('common.table.Voice Provider')}`,
                        placeholder: t('common.table.Voice Provider'),
                      }}
                      sx={{ minWidth: '24.8rem', maxWidth: '24.8rem' }}
                      onChange={handleVoiceProviderChange}
                    />
                    {isVonageProvider && (
                      <StyledRadioGroup
                        row
                        aria-label="vonage-mode"
                        name="vonageModeRadioBtn"
                        value={vonageMode}
                        onChange={handleVonageModeChange}
                        sx={{ marginTop: '3rem', marginLeft: 0 }}
                      >
                        <Tooltip title={oldVonageTooltip} placement="top-start">
                          <FormControlLabel
                            value={VonageMode.OLD}
                            control={<Radio />}
                            label={t('userCallerIds.Vonage Mode Old')}
                          />
                        </Tooltip>
                        <Tooltip title={newVonageTooltip} placement="top-end">
                          <FormControlLabel
                            value={VonageMode.NEW}
                            control={<Radio />}
                            label={t('userCallerIds.Vonage Mode New')}
                          />
                        </Tooltip>
                      </StyledRadioGroup>
                    )}
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                      borderTop: '0.1rem solid #A3ABB2',
                      padding: '3rem 2.4rem 2.4rem',
                    }}
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Button type="submit" color="primary">
                        {t('common.messages.Submit')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <FullScreenLoader />
              )}
            </>
          )}
          {isSuccessfullyModified && (
            <>
              {!isLoading ? (
                successfullyModifiedInfoSection()
              ) : (
                <FullScreenLoader />
              )}
            </>
          )}
        </FormProvider>
      </Modal>
    </>
  );
};

export default VoiceProviderModal;
